/**
 * The reminder cycle: run it periodically (and when tasks change). It turns due
 * reminder occurrences into notifications exactly once, marks the ones that came
 * due while the app was closed as missed, and handles snoozes and summaries.
 * Completing, changing or deleting a task cancels its reminders implicitly:
 * occurrences are recomputed from the current tasks every cycle.
 */
import {
  inQuietHours,
  isOpen,
  localDate,
  occurrences,
  snoozeUntil,
  summaryOccurrences,
  type NotificationSettings,
  type SnoozeChoice,
} from '@nexus/core';
import type { NotificationRecord, Store } from '@nexus/store';
import { loadTasks, updateTask, type Task } from './tasks';
import { finishMemoReminder, isMemoPage, loadMemos, memoReminderTasks } from './memos';

/** A reminder that is at most this late still counts as on time. */
export const GRACE_MS = 15 * 60_000;

export interface CycleText {
  /** Body for a task reminder, e.g. "Vandaag · in Vergadering". */
  reminderBody(task: Task): string;
  /** Body for an "Onthouden" reminder. */
  memoBody?(): string;
  dailyTitle(counts: { today: number; overdue: number }): string;
  weeklyTitle(counts: { nextWeek: number; overdue: number }): string;
}

export interface Delivered {
  notification: NotificationRecord;
  /** Show it as an OS notification / toast now (false for missed or quiet hours). */
  announce: boolean;
}

const SINCE_KEY = 'reminders_since';

type MemoTask = ReturnType<typeof memoReminderTasks>[number];
const isTask = (t: Task | MemoTask): t is Task => 'kind' in t;

/** Tasks plus "Onthouden" items with a reminder, for the reminder rules. */
async function remindable(store: Store) {
  const tasks = await loadTasks(store);
  const memos = memoReminderTasks(await loadMemos(store));
  const byId = new Map<string, Task | MemoTask>([...tasks, ...memos].map((t) => [t.id, t]));
  return { tasks, all: [...tasks, ...memos], byId };
}

const bodyOf = (task: Task | MemoTask, text: CycleText) =>
  isTask(task) ? text.reminderBody(task) : (text.memoBody?.() ?? '');

/** Reminders from before the feature was first used are never replayed. */
async function since(store: Store, now: number): Promise<number> {
  const v = await store.getSetting(SINCE_KEY);
  if (v) return Number(v);
  await store.setSetting(SINCE_KEY, String(now));
  return now;
}

export async function runReminderCycle(
  store: Store,
  settings: NotificationSettings,
  text: CycleText,
  now = Date.now(),
): Promise<Delivered[]> {
  if (!settings.enabled) return [];
  const from = await since(store, now);
  const { tasks, all, byId } = await remindable(store);
  const states = await store.reminderStates();
  const quiet = inQuietHours(now, settings);
  const delivered: Delivered[] = [];

  for (const occ of occurrences(all, settings)) {
    if (occ.fireAt > now || occ.fireAt < from) continue;
    const state = states.get(occ.key);
    const snoozedBack = state?.status === 'snoozed' && (state.snoozedUntil ?? Infinity) <= now;
    if (state && !snoozedBack) continue;
    const task = byId.get(occ.taskId)!;
    const missed = !snoozedBack && now - occ.fireAt > GRACE_MS;
    const notification = await store.addNotification({
      key: occ.key,
      kind: missed ? 'missed' : 'reminder',
      taskId: task.id,
      pageId: task.pageId,
      title: task.title,
      body: bodyOf(task, text),
      fireAt: occ.fireAt,
    });
    await store.setReminderState({
      key: occ.key,
      taskId: task.id,
      status: missed ? 'missed' : 'sent',
      snoozedUntil: null,
    });
    delivered.push({ notification, announce: !missed && !quiet });
    // A recurring memo moves on to its next date (a one-off keeps its, as a record).
    if (!isTask(task) && !snoozedBack) {
      const memo = (await loadMemos(store)).find((m) => m.id === task.id);
      if (memo?.recurrence) await finishMemoReminder(store, task.id, now);
    }
  }

  const today = localDate(now);
  for (const sum of summaryOccurrences(now, settings)) {
    // Once per day (or week): every cycle sees the same occurrence until midnight.
    if (sum.fireAt > now || sum.fireAt < from || states.has(sum.key)) continue;
    const open = tasks.filter((t) => isOpen(t.status));
    const overdue = open.filter((t) => t.due && t.due < today).length;
    let title: string;
    if (sum.kind === 'daily') {
      title = text.dailyTitle({ today: open.filter((t) => t.due === today).length, overdue });
    } else {
      const week = new Date(now);
      week.setDate(week.getDate() + 7);
      const end = localDate(week.getTime());
      title = text.weeklyTitle({
        nextWeek: open.filter((t) => t.due && t.due > today && t.due <= end).length,
        overdue,
      });
    }
    const notification = await store.addNotification({
      key: sum.key,
      kind: 'summary',
      taskId: null,
      pageId: null,
      title,
      body: '',
      fireAt: sum.fireAt,
    });
    await store.setReminderState({ key: sum.key, taskId: '', status: 'sent', snoozedUntil: null });
    delivered.push({ notification, announce: !quiet && now - sum.fireAt <= GRACE_MS * 8 });
  }
  return delivered;
}

// ---- actions from a notification ------------------------------------------------------

export async function completeFromNotification(store: Store, n: NotificationRecord): Promise<void> {
  if (!n.taskId) return;
  const task = (await loadTasks(store)).find((t) => t.id === n.taskId);
  if (task && isOpen(task.status)) await updateTask(store, task, { status: 'done' });
  else if (!task && (await isMemoPage(store, n.taskId))) await finishMemoReminder(store, n.taskId);
  await store.markNotificationsRead([n.id]);
}

export async function snoozeNotification(
  store: Store,
  n: NotificationRecord,
  choice: SnoozeChoice,
  settings: NotificationSettings,
  now = Date.now(),
): Promise<number> {
  const until = snoozeUntil(choice, now, settings);
  await store.setReminderState({
    key: n.key,
    taskId: n.taskId ?? '',
    status: 'snoozed',
    snoozedUntil: until,
  });
  await store.markNotificationsRead([n.id]);
  return until;
}

// ---- push schedule (reminders while the app is closed) ----------------------------------

export interface PushItem {
  /** Same key as the local notification, so the OS shows only one of both. */
  key: string;
  fireAt: number;
  title: string;
  body: string;
  pageId: string | null;
  taskId: string | null;
}

/** How far ahead the cloud gets the reminder moments. */
export const PUSH_HORIZON_MS = 7 * 24 * 3600_000;

/**
 * Reminder moments for the next week, for Web Push. Moments in the quiet hours
 * are left out (the app shows them as missed, like local reminders). Summaries
 * carry the counts as they are now. Whether this device already showed a
 * reminder does not matter: the cloud pushes each key once, to all devices.
 */
export async function pushSchedule(
  store: Store,
  settings: NotificationSettings,
  text: CycleText,
  now = Date.now(),
): Promise<PushItem[]> {
  if (!settings.enabled || !settings.system) return [];
  const from = now - 5 * 60_000;
  const until = now + PUSH_HORIZON_MS;
  const { tasks, all, byId } = await remindable(store);
  const items: PushItem[] = [];
  for (const occ of occurrences(all, settings)) {
    if (occ.fireAt <= from || occ.fireAt > until || inQuietHours(occ.fireAt, settings)) continue;
    const task = byId.get(occ.taskId)!;
    items.push({
      key: occ.key,
      fireAt: occ.fireAt,
      title: task.title,
      body: bodyOf(task, text),
      pageId: task.pageId,
      taskId: task.id,
    });
  }
  const open = tasks.filter((t) => isOpen(t.status));
  for (let day = 0; day < 8; day++) {
    const d = new Date(now);
    d.setDate(d.getDate() + day);
    const at = d.getTime();
    for (const sum of summaryOccurrences(at, settings)) {
      if (sum.fireAt <= from || sum.fireAt > until) continue;
      if (inQuietHours(sum.fireAt, settings)) continue;
      const date = localDate(at);
      const overdue = open.filter((t) => t.due && t.due < date).length;
      let title: string;
      if (sum.kind === 'daily') {
        title = text.dailyTitle({ today: open.filter((t) => t.due === date).length, overdue });
      } else {
        const week = new Date(at);
        week.setDate(week.getDate() + 7);
        const end = localDate(week.getTime());
        title = text.weeklyTitle({
          nextWeek: open.filter((t) => t.due && t.due > date && t.due <= end).length,
          overdue,
        });
      }
      items.push({ key: sum.key, fireAt: sum.fireAt, title, body: '', pageId: null, taskId: null });
    }
  }
  return items.sort((a, b) => a.fireAt - b.fireAt);
}
